/**
 * TA Cockpit → Tracking. Is the trimester's hiring plan being delivered?
 *
 * ⚠️ IT NO LONGER READS THE GOOGLE SHEET. Every figure here used to come from a
 * named range on the TA Cockpit sheet that somebody had to keep up to date —
 * `target_total`, `hired_total`, `hired_growth`, `ref_expected_pace_pct`, and
 * three `planning_*` cells behind the old Planning-accuracy chart. They went
 * stale the same way `ref_trimester_m1` did: it still said "May 2026" in
 * September, and because the demand board read it, "hired this trimester"
 * counted every offer since MAY — 77 instead of 9.
 *
 * The same facts are all live now:
 *   the trimester and the expected pace from the CALENDAR,
 *   hires and open demand from the HIRING DATABASE,
 *   the trimester's target from the FROZEN BASELINE of that table,
 *   the cluster split from `People Team`, the rule Role Prioritisation uses.
 *
 * Consequence worth knowing: this tab now survives a sheet outage, so
 * `TACockpitView` no longer gates it behind `fetchTACockpitData`. Planning is the
 * only tab left that needs the sheet.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchTracking } from '../../../services/taTrackingApi';
import type { TrackingData } from '../../../types/taTracking';
import { KpiTile, pct, pctRaw } from './trackingUi';
import { ClusterProgress } from './ClusterProgress';
import { NoBaseline, PlanVsNowSection } from './PlanVsNowSection';
import { TileSection, TileStrip, useTileSections } from './DeliveryTiles';
import { buildTiles } from './deliveryTileSpecs';
import { OfferHygiene } from './OfferHygiene';
import { StageFunnel } from './StageFunnel';
import { StartAccuracySection } from './StartAccuracySection';
import { TrendsSection } from './TrendsSection';
import { useCandidateFunnel } from './useCandidateFunnel';
import { usePipelineTrends } from './usePipelineTrends';
import { startAccuracy } from '../../../utils/deliveryTiles';
import { momentum, movements, trendShape } from '../../../utils/pipelineTrends';

export function TrackingTab({ period }: { period: string }) {
  const [data, setData] = useState<TrackingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /*
   * ⚠️ THREE SOURCES, THREE FAILURE MODES, ONE LAYOUT. The delivery figures come from the Hiring
   * Database; the candidate funnel and the weekly series come from the warehouse through two
   * different queries. Every section below is built from a nullable, so no one of them going down
   * blanks the others — what used to be an early return is now a dash on a tile and a line inside
   * its own section.
   */
  const funnel = useCandidateFunnel();
  const trends = usePipelineTrends();
  const { open, toggle, register } = useTileSections();

  const refresh = useCallback(
    () =>
      fetchTracking(period)
        .then((d) => {
          setData(d);
          setError(null);
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false)),
    [period],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function reload() {
    setLoading(true);
    void refresh();
  }

  if (loading && !data) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading the Hiring Database…
      </div>
    );
  }

  /*
   * ⚠️ NO EARLY RETURN ON A TRACKING FAILURE any more, and that is deliberate. Two of the five
   * tiles and two whole sections read the warehouse, not the Hiring Database — returning here
   * would blank them for an outage somewhere else. The HDB tiles show a dash, this box says what
   * failed, and the pipeline sections carry on.
   */
  const progress = data && data.target.heads > 0 ? data.hired / data.target.heads : 0;
  const expected = data?.pace.share ?? 0;
  const frozen = data?.target.source === 'baseline';

  const thisMonth = new Date().toISOString().slice(0, 7);
  /*
   * Start accuracy is measured over the roles the FROZEN plan is being tracked against, so it has
   * the same population as the waterfall above it. With no baseline there is nothing to be
   * accurate against — the tile says so rather than measuring the live table against itself.
   */
  const accuracy = startAccuracy(data?.planVsNow ? planRoles(data.planVsNow) : [], thisMonth);
  const moves = movements(trends.data);
  const pace = momentum(moves);
  // One shape, read by the tile and by its own section — see the note in `deliveryTileSpecs`.
  const shape = trendShape(moves);
  // "No frozen plan" and "no roles with a start" are different facts — see `deliveryTileSpecs`.
  const hasBaseline = Boolean(data?.planVsNow && data.target.baseline);
  const tiles = buildTiles({
    data,
    accuracy,
    hasBaseline,
    momentum: pace,
    shape,
    trends: { loading: trends.loading, error: trends.error },
    pulse: funnel.data?.offers,
  });

  return (
    <div className="space-y-5">
      {/*
        ⚠️ A FAILED REFRESH IS NOT A SILENT ONE. `data` survives a failed reload,
        so the error screen below never renders and the figures stay on screen —
        under a line that calls them live. Stale numbers presented as live is the
        worst of the three possible states, so the banner says which load failed
        and when the figures on screen are actually from.
      */}
      {error && data && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <b className="font-semibold">These figures are not live.</b> The last refresh failed
            ({error}), so what is below is from{' '}
            {new Date(data.generatedAt).toLocaleTimeString('en-GB')}.
          </div>
        </div>
      )}

      {data?.target.baselineError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <b className="font-semibold">The frozen plan could not be read.</b>{' '}
            {data.target.baselineError}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {data ? (
            <>
              {data.label} · day {data.pace.elapsedDays} of {data.pace.totalDays} · hiring for
              starts up to {monthName(data.windowEnd)} ·{' '}
              <b className="font-semibold text-gray-700">
                {error ? 'last read from the Hiring Database' : 'live from the Hiring Database'}
              </b>
            </>
          ) : (
            <b className="font-semibold text-gray-700">the Hiring Database could not be read</b>
          )}
        </p>
        <Button variant="secondary" size="sm" onClick={reload} disabled={loading}>
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {!data && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white py-10 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5" />
          <div>Could not load tracking: {error}</div>
          <p className="max-w-md text-center text-gray-500">
            The delivery figures come from the Hiring Database. The pipeline sections below read the
            warehouse and are unaffected.
          </p>
          <Button variant="secondary" size="sm" onClick={reload}>
            Retry
          </Button>
        </div>
      )}

      {data && (
      <div className="flex overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <KpiTile
          testId="tracking-target"
          label="Target"
          value={data.target.heads}
          note={
            frozen
              ? `frozen plan · ${data.target.baseline?.rowCount ?? 0} rows`
              : 'today’s table — no baseline frozen'
          }
          tone={frozen ? 'neutral' : 'warn'}
          why={
            frozen
              ? `Roles the FROZEN BASELINE had starting inside the HIRING WINDOW — this trimester plus the ${data.noticeMonths}-month average notice period, so every start up to ${monthName(data.windowEnd)} — including any already overdue when it was taken. A start is offered about a notice period before the first day, which is why the window runs past the trimester. On-hold and undated roles are not in it: one is paused on purpose, the other belongs to no trimester.`
              : 'Nobody has frozen a baseline for this period, so this is TODAY’s table: what is still to recruit plus what has already been won this trimester. It moves whenever the plan does, which is exactly what a target should not do — freeze a baseline on Planning → Hiring demand.'
          }
        />
        <KpiTile
          testId="tracking-hired"
          label="Hired"
          value={data.hired}
          note="offers accepted this trimester"
          why="Roles whose status is hired and whose OFFER WAS ACCEPTED in one of this trimester’s four months. Counted on the offer date, not the start date — a hire is won when it is signed, and counting by start date both delays it and loses anyone signed but not yet begun. An offer signed this trimester for a start next year counts here, which is why Hired is not strictly a subset of Target."
        />
        <KpiTile
          testId="tracking-progress"
          label="Progress"
          value={pctRaw(progress)}
          note={`vs ${pct(expected)} of the trimester gone`}
          tone={progress >= expected ? 'ok' : 'warn'}
          why="Hired ÷ Target, against the share of the trimester the CALENDAR has used up. That pace used to be a percentage typed into the sheet; it is now days elapsed ÷ days in the trimester, so it cannot go stale."
        />
        <KpiTile
          testId="tracking-to-recruit"
          label="Still to recruit"
          value={data.toRecruit}
          note={data.onHold > 0 ? `${data.onHold} more on hold` : undefined}
          why={`Live HDB roles that are OPEN or BACKLOGGED and starting inside the hiring window — this trimester plus the ${data.noticeMonths}-month notice period, so every start up to ${monthName(data.windowEnd)} is counted here — including ones already overdue. Excludes ON HOLD roles, paused on purpose and re-dated when recruiting resumes, and excludes the Initiative application pipeline, which is not a seat.`}
        />
        <BackfillTile backfills={data.backfills} />
        <KpiTile
          testId="tracking-undated"
          label="No date yet"
          value={data.undated}
          note="demand in no window"
          tone={data.undated > 0 ? 'warn' : 'neutral'}
          why="Roles that are open or backlogged but carry no planned, adjusted or actual start date. They are real demand and they are in no trimester’s target — a role with no date cannot be said to be due by any month, so it cannot be inside or outside the hiring window either. Put a planned start on the row in Airtable."
        />
      </div>
      )}

      {/*
        ⚠️ THE TILES ARE THE SECTIONS, compressed. Each opens its own detail below and scrolls to
        it, one at a time — several open at once is the stack of sections this replaced, and the
        link between a tile and its evidence stops being visible the moment two are showing.
      */}
      <TileStrip tiles={tiles} open={open} onToggle={toggle} />

      <div className="space-y-5">
        <TileSection id="plan" open={open === 'plan'} register={register('plan')}>
          {data?.planVsNow && data.target.baseline ? (
            <PlanVsNowSection
              data={data.planVsNow}
              windowEndLabel={monthName(data.windowEnd)}
              noticeMonths={data.noticeMonths}
              takenAt={data.target.baseline.takenAt}
              takenBy={data.target.baseline.takenBy}
              label={data.target.baseline.label}
            />
          ) : (
            <NoBaseline />
          )}
        </TileSection>

        <TileSection
          id="starts"
          open={open === 'starts'}
          register={register('starts')}
        >
          <StartAccuracySection accuracy={accuracy} hasBaseline={hasBaseline} />
        </TileSection>

        <TileSection
          id="prospects"
          open={open === 'prospects'}
          register={register('prospects')}
        >
          <div className="space-y-5">
            <StageFunnel data={funnel.data} error={funnel.error} loading={funnel.loading} />
            {/*
              The tidy-up, last and shut. It is the OPPOSITE population from the offer tile inside
              the funnel — every posting, open or closed — because what needs archiving is
              precisely the records on roles nobody is working any more.
            */}
            <OfferHygiene pulse={funnel.data?.offers} />
          </div>
        </TileSection>

        <TileSection
          id="trends"
          open={open === 'trends'}
          register={register('trends')}
        >
          <TrendsSection
            moves={moves}
            shape={shape}
            loading={trends.loading}
            error={trends.error}
          />
        </TileSection>

        <TileSection
          id="clusters"
          open={open === 'clusters'}
          register={register('clusters')}
        >
          {data ? (
            <ClusterProgress
              clusters={data.clusters}
              expectedPace={expected}
              unresolved={data.attribution.unresolved}
            />
          ) : null}
        </TileSection>
      </div>

      {/*
        ⚠️ "Where these figures come from" is GONE (Theresa, Sep 22 2026). Every
        figure carries its own definition on its own `i`, which is where somebody
        checking one actually looks — a section restating four of them at the
        bottom was read by nobody and still had to be kept in step with the
        tiles. What it carried that nothing else did was the unmapped-column
        warning, so that stays, as a line rather than a section.
      */}
      {data && data.hdbUnmappedFields.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          <b>Some HDB columns could not be matched:</b> {data.hdbUnmappedFields.join(', ')}. Figures
          that depend on them read low.
        </p>
      )}
    </div>
  );
}

/**
 * Every role the frozen plan is still being tracked against, as one list.
 *
 * ⚠️ Deliberately NOT `dropped` — a role that left the plan has no start to be accurate about —
 * and deliberately including `filled`, which is where the only settled answers are.
 */
function planRoles(plan: NonNullable<TrackingData['planVsNow']>) {
  return [...plan.filled, ...plan.redated, ...plan.onHold, ...plan.movedIn, ...plan.added].map((r) => ({
    positionId: r.positionId,
    title: r.title,
    recruiter: r.recruiter,
    plannedMonth: r.plannedMonth,
    actualMonth: r.actualMonth,
    month: r.month,
    status: r.status,
  }));
}

function BackfillTile({ backfills: b }: { backfills: TrackingData['backfills'] }) {
  const pctOf = (v: number | null) => (v === null ? null : `${(v * 100).toFixed(1)}%`);
  const hotter = b.lastYear !== null && b.raised > b.lastYear;

  const note = b.lastYearAvailable
    ? `${b.lastYear} a year ago${b.rate !== null ? ` · ${pctOf(b.rate)} of headcount` : ''}`
    : 'no comparable window a year ago';

  return (
    <KpiTile
      testId="tracking-backfills"
      label="Backfills"
      value={b.raised}
      note={note}
      tone={hotter ? 'warn' : 'neutral'}
      why={
        'Roles in this hiring window that exist because somebody left — filled ones included, ' +
        'because a backfill that has been filled still happened. ' +
        (b.raisedSinceBaseline !== null
          ? `${b.raisedSinceBaseline} of them were raised since the plan was frozen. `
          : '') +
        (b.lastYearAvailable
          ? `The comparison is COUNT against COUNT over the SAME window one year earlier, out of the Hiring Database's own history: ${b.lastYear} then. ` +
            (b.headcount
              ? `That is ${pctOf(b.rate)} of today's ${b.headcount} internal headcount — scale, not a second comparison: the people database keeps no historical headcount, so a rate for last year could only divide last year's count by this year's people. `
              : '')
          : 'The Hiring Database holds no rows in the comparable window a year ago, so there is no expectation to compare against — which is not the same as a year in which nobody left. ') +
        ` ${b.newRoles} roles in the same window are growth rather than attrition.`
      }
    />
  );
}

/** `2027-03` → `March 2027`, for a window end read in prose. */
function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
